import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronRightIcon,
  EllipsisVerticalIcon,
  FolderIcon,
  FolderPlusIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  ArrowsRightLeftIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  DocumentArrowUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { atsApi } from '../../ats/services/atsApi';
import { FolderDeleteModal } from '../../ats/components/FolderDeleteModal';
import { FolderMoveModal } from '../../ats/components/FolderMoveModal';
import { useAtsNavigationStore } from '../../ats/store/atsNavigationStore';
import type { AtsCandidateListItem } from '../../ats/types';
import {
  ALL_COUNTRIES_FOLDER,
  buildFolderTreeFromPaths,
  folderPathMatches,
  filterFolderTree,
  isAllCountriesFolderName,
  type AtsFolderNode,
} from '../../ats/utils/folders';

const collectExpandablePaths = (nodes: AtsFolderNode[]): string[] => nodes.flatMap((node) => [
  ...(node.children.length > 0 ? [node.path] : []),
  ...collectExpandablePaths(node.children),
]);

const FolderTreeItem = ({
  node,
  level,
  activeFolder,
  expandedPaths,
  onSelect,
  onToggle,
  onRename,
  onDelete,
  onCreateChild,
  onMove,
  onImport,
  openMenuPath,
  onToggleMenu,
  searching,
}: {
  node: AtsFolderNode;
  level: number;
  activeFolder: string;
  expandedPaths: Set<string>;
  onSelect: (folder: string) => void;
  onToggle: (folder: string) => void;
  onRename: (folder: AtsFolderNode) => void;
  onDelete: (folder: AtsFolderNode) => void;
  onCreateChild: (folder: AtsFolderNode) => void;
  onMove: (folder: AtsFolderNode) => void;
  onImport: (folder: AtsFolderNode) => void;
  openMenuPath: string | null;
  onToggleMenu: (folderPath: string) => void;
  searching: boolean;
}) => {
  const active = folderPathMatches(node.path, activeFolder, []);
  const hasChildren = node.children.length > 0;
  const expanded = expandedPaths.has(node.path);
  const isAllCountriesRoot = node.path === ALL_COUNTRIES_FOLDER;
  const canDelete = !isAllCountriesRoot;
  const canRename = !isAllCountriesRoot;
  const canMove = node.path.split('/').length > 1;
  const canCreateChild = isAllCountriesRoot || node.path.split('/').length < 3;
  const canImport = !isAllCountriesRoot;

  return (
    <div>
      <div
        role="button"
        aria-label={node.name}
        tabIndex={0}
        onClick={() => onSelect(node.path)}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onSelect(node.path);
          }
        }}
        className={`flex min-h-11 w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[15px] transition-colors ${
          active
            ? 'bg-white font-semibold text-[var(--text-dark)] shadow-sm'
            : 'text-[var(--text-medium)] hover:bg-white/70'
        }`}
        style={{ paddingLeft: `${12 + level * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={expanded ? 'Свернуть папку' : 'Развернуть папку'}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.path);
            }}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md hover:bg-[#EEF0F2]"
          >
            <ChevronRightIcon className={`h-4 w-4 text-[var(--text-tertiary)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        ) : null}
        <FolderIcon className="h-5 w-5 shrink-0 fill-[#F6A21A] text-[#F6A21A]" />
        <span title={node.path} className={`min-w-0 flex-1 ${searching ? 'break-words' : 'truncate'}`}>{node.name}</span>
        {node.count > 0 && (
          <span className="shrink-0 text-[13px] text-[var(--text-tertiary)]">{node.count}</span>
        )}
        <span className="relative flex shrink-0 items-center">
          <button
            type="button"
            aria-label={`Действия с папкой ${node.name}`}
            aria-expanded={openMenuPath === node.path}
            onClick={(event) => { event.stopPropagation(); onToggleMenu(node.path); }}
            className="rounded p-1 text-[var(--text-tertiary)] hover:bg-[#EEF0F2] hover:text-[var(--text-dark)]"
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>
          {openMenuPath === node.path && (
            <div
              role="menu"
              onClick={(event) => event.stopPropagation()}
              className="absolute right-0 top-7 z-30 w-56 rounded-lg border border-[#E4E6EA] bg-white p-1 shadow-[0_10px_24px_rgba(21,22,24,0.16)]"
            >
              {canCreateChild && <button type="button" role="menuitem" onClick={() => onCreateChild(node)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] hover:bg-[#F5F4EF]"><FolderPlusIcon className="h-4 w-4" />Добавить подпапку</button>}
              {canImport && <button type="button" role="menuitem" onClick={() => onImport(node)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] hover:bg-[#F5F4EF]"><DocumentArrowUpIcon className="h-4 w-4" />Импортировать сюда</button>}
              {canMove && <button type="button" role="menuitem" onClick={() => onMove(node)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] hover:bg-[#F5F4EF]"><ArrowsRightLeftIcon className="h-4 w-4" />Перенести</button>}
              {canRename && <button type="button" role="menuitem" onClick={() => onRename(node)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] hover:bg-[#F5F4EF]"><PencilIcon className="h-4 w-4" />Переименовать</button>}
              {canDelete && (
                <button type="button" role="menuitem" onClick={() => onDelete(node)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] text-[#D92D20] hover:bg-[#FFF1F1]"><TrashIcon className="h-4 w-4 shrink-0" />Удалить</button>
              )}
            </div>
          )}
        </span>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <FolderTreeItem
              key={child.path}
              node={child}
              level={level + 1}
              activeFolder={activeFolder}
              expandedPaths={expandedPaths}
              onSelect={onSelect}
              onToggle={onToggle}
              onRename={onRename}
              onDelete={onDelete}
              onCreateChild={onCreateChild}
              onMove={onMove}
              onImport={onImport}
              openMenuPath={openMenuPath}
              onToggleMenu={onToggleMenu}
              searching={searching}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export function SidebarAtsFolders() {
  const selectedFolder = useAtsNavigationStore((state) => state.selectedFolder);
  const setSelectedFolder = useAtsNavigationStore((state) => state.setSelectedFolder);
  const isAtsSidebarExpanded = useAtsNavigationStore((state) => state.isAtsSidebarExpanded);
  const toggleAtsSidebarExpanded = useAtsNavigationStore((state) => state.toggleAtsSidebarExpanded);
  const notifyCandidatesUpdated = useAtsNavigationStore((state) => state.notifyCandidatesUpdated);
  const refreshVersion = useAtsNavigationStore((state) => state.refreshVersion);
  const [savedFolderPaths, setSavedFolderPaths] = useState<string[]>([]);
  const [folderCounts, setFolderCounts] = useState<Record<string, number>>({});
  const [folderTotal, setFolderTotal] = useState(0);
  const [deleteModalCandidates, setDeleteModalCandidates] = useState<AtsCandidateListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [sortMode, setSortMode] = useState<'updated' | 'nameAsc' | 'nameDesc'>('updated');
  const [sortOpen, setSortOpen] = useState(false);
  const [folderQuery, setFolderQuery] = useState('');
  const [searchCollapsedPaths, setSearchCollapsedPaths] = useState<Set<string>>(new Set());
  const [openMenuPath, setOpenMenuPath] = useState<string | null>(null);
  const [folderAction, setFolderAction] = useState<{ type: 'create'; folder: AtsFolderNode | null } | null>(null);
  const [folderToMove, setFolderToMove] = useState<AtsFolderNode | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<AtsFolderNode | null>(null);
  const [folderName, setFolderName] = useState('');
  const expansionInitialized = useRef(false);
  const folders = useMemo(
    () => buildFolderTreeFromPaths(savedFolderPaths, folderCounts),
    [folderCounts, savedFolderPaths],
  );
  const foldersWithAllCountries = useMemo((): AtsFolderNode[] => ([
    {
      id: ALL_COUNTRIES_FOLDER,
      name: ALL_COUNTRIES_FOLDER,
      path: ALL_COUNTRIES_FOLDER,
      count: folderTotal,
      children: [],
    },
    // Real DB folder «Все страны» is remapped in buildFolderTree; keep a hard filter as safety.
    ...folders.filter((folder) => folder.path !== ALL_COUNTRIES_FOLDER),
  ]), [folderTotal, folders]);
  const sortedFolders = useMemo(() => {
    const sortNodes = (nodes: AtsFolderNode[]): AtsFolderNode[] => (
      [...nodes]
        .map((node) => ({ ...node, children: sortNodes(node.children) }))
        .sort((left, right) => {
          if (left.path === ALL_COUNTRIES_FOLDER) return -1;
          if (right.path === ALL_COUNTRIES_FOLDER) return 1;
          if (sortMode === 'updated') {
            return right.count - left.count;
          }

          const byName = left.name.localeCompare(right.name, 'ru');
          return sortMode === 'nameDesc' ? -byName : byName;
        })
    );

    return sortNodes(foldersWithAllCountries);
  }, [foldersWithAllCountries, sortMode]);
  const searching = Boolean(folderQuery.trim());
  const visibleFolders = useMemo(() => filterFolderTree(sortedFolders, folderQuery), [sortedFolders, folderQuery]);
  const visibleExpandedPaths = useMemo(() => searching
    ? new Set(collectExpandablePaths(visibleFolders).filter((path) => !searchCollapsedPaths.has(path)))
    : expandedPaths, [searching, visibleFolders, searchCollapsedPaths, expandedPaths]);
  const changeFolderQuery = (value: string) => {
    setFolderQuery(value);
    setSearchCollapsedPaths(new Set());
    setOpenMenuPath(null);
  };
  const activeFolder = selectedFolder || sortedFolders[0]?.path || '';
  useEffect(() => {
    if (expansionInitialized.current || sortedFolders.length === 0) return;
    expansionInitialized.current = true;
    setExpandedPaths(new Set(collectExpandablePaths(sortedFolders)));
  }, [sortedFolders]);

  useEffect(() => {
    let mounted = true;

    const loadFolders = async () => {
      setLoading(true);
      setError(null);
      try {
        const catalog = await atsApi.listFolders();
        if (mounted) {
          setSavedFolderPaths(catalog.paths);
          setFolderCounts(catalog.counts);
          setFolderTotal(catalog.total);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Не удалось загрузить папки');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadFolders();

    return () => {
      mounted = false;
    };
  }, [refreshVersion]);

  const selectFolder = (folder: string) => {
    setSelectedFolder(folder, true);
  };

  const toggleFolder = (folder: string) => {
    if (searching) {
      setSearchCollapsedPaths((current) => {
        const next = new Set(current);
        if (next.has(folder)) next.delete(folder); else next.add(folder);
        return next;
      });
      return;
    }
    setExpandedPaths((current) => {
      const next = new Set(current);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  const reloadFolders = async () => {
    const catalog = await atsApi.listFolders();
    setSavedFolderPaths(catalog.paths);
    setFolderCounts(catalog.counts);
    setFolderTotal(catalog.total);
    notifyCandidatesUpdated();
    return catalog;
  };

  const renameFolder = async (folder: AtsFolderNode) => {
    setOpenMenuPath(null);
    if (folder.path === ALL_COUNTRIES_FOLDER) {
      setError(`Папку «${folder.name}» переименовать нельзя`);
      return;
    }
    const name = window.prompt('Новое название папки', folder.name)?.trim();
    if (!name || name === folder.name) return;
    setLoading(true);
    setError(null);
    try {
      await atsApi.renameFolder(folder.path, name);
      const parentPath = folder.path.split('/').slice(0, -1).join('/');
      setSelectedFolder(parentPath ? `${parentPath}/${name}` : name);
      await reloadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось переименовать папку');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteFolder = (folder: AtsFolderNode) => {
    setOpenMenuPath(null);
    if (folder.path === ALL_COUNTRIES_FOLDER) {
      setError(`Папку «${folder.name}» удалить нельзя`);
      return;
    }
    setFolderToDelete(folder);
    setDeleteModalCandidates([]);
    void atsApi.listAllCandidates({ folder_path: folder.path, page_size: 100 })
      .then(setDeleteModalCandidates)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Не удалось загрузить кандидатов папки');
      });
  };

  const deleteFolderWithResumes = async () => {
    if (!folderToDelete) return;
    setLoading(true);
    setError(null);
    try {
      await atsApi.deleteFolder(folderToDelete.path, { withResumes: true });
      const parentPath = folderToDelete.path.split('/').slice(0, -1).join('/');
      setSelectedFolder(parentPath || ALL_COUNTRIES_FOLDER);
      setFolderToDelete(null);
      await reloadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить папку');
    } finally {
      setLoading(false);
    }
  };

  const moveSelectedAndDeleteFolder = async (candidateIds: string[], targetFolderPath: string) => {
    if (!folderToDelete) return;
    setLoading(true);
    setError(null);
    try {
      await Promise.all(candidateIds.map((candidateId) => atsApi.moveCandidateFolder(candidateId, targetFolderPath)));
      await atsApi.deleteFolder(folderToDelete.path, { withResumes: true });
      setSelectedFolder(targetFolderPath);
      setFolderToDelete(null);
      await reloadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить папку');
    } finally {
      setLoading(false);
    }
  };

  const openCreateFolder = (folder: AtsFolderNode | null) => {
    setOpenMenuPath(null);
    setFolderName('');
    setFolderAction({ type: 'create', folder });
  };

  const openMoveFolder = (folder: AtsFolderNode) => {
    setOpenMenuPath(null);
    setFolderToMove(folder);
  };

  const importIntoFolder = (folder: AtsFolderNode) => {
    setOpenMenuPath(null);
    setSelectedFolder(folder.path);
    window.dispatchEvent(new CustomEvent('ats:import-into-folder', { detail: { folderPath: folder.path } }));
  };

  const submitFolderCreate = async () => {
    if (!folderAction) return;
    if (isAllCountriesFolderName(folderName) && !folderAction.folder) {
      setError(`Папку «${ALL_COUNTRIES_FOLDER}» создать нельзя`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await atsApi.createFolder(folderAction.folder?.path || null, folderName.trim());
      setSelectedFolder(folderAction.folder ? `${folderAction.folder.path}/${folderName.trim()}` : folderName.trim());
      await reloadFolders();
      setFolderAction(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить папку');
    } finally {
      setLoading(false);
    }
  };

  const submitFolderMove = async (targetParentPath: string) => {
    if (!folderToMove) return;
    setLoading(true);
    setError(null);
    try {
      await atsApi.moveFolder(folderToMove.path, targetParentPath);
      setSelectedFolder(`${targetParentPath}/${folderToMove.name}`);
      setFolderToMove(null);
    } catch (err) {
      // A proxy timeout can arrive after the server committed the move.
      // Read back the catalog before inviting a second (possibly duplicate) request.
      const sourcePath = folderToMove.path;
      const targetPath = `${targetParentPath}/${folderToMove.name}`;
      try {
        const catalog = await atsApi.listFolders();
        setSavedFolderPaths(catalog.paths);
        setFolderCounts(catalog.counts);
        setFolderTotal(catalog.total);
        const hasSource = catalog.paths.some((path) => path === sourcePath || path.startsWith(`${sourcePath}/`));
        const hasTarget = catalog.paths.some((path) => path === targetPath || path.startsWith(`${targetPath}/`));
        if (!hasSource && hasTarget) {
          setSelectedFolder(targetPath);
          setFolderToMove(null);
          notifyCandidatesUpdated();
          setLoading(false);
          return;
        }
      } catch (_) {
        // Keep the original mutation error when the catalog is unavailable too.
      }
      if (err instanceof Error && /\b(?:503|504)\b/.test(err.message)) {
        setFolderToMove(null);
        setError('Не удалось подтвердить перенос (503/504). Проверьте список папок и обновите страницу перед повторной попыткой: операция могла завершиться на сервере.');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось перенести папку');
      }
      setLoading(false);
      return;
    }
    try {
      await reloadFolders();
    } catch (err) {
      setError(err instanceof Error ? `Папка перенесена, но список не обновился: ${err.message}` : 'Папка перенесена, но список не обновился');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col border-t border-sidebar-border px-5 pt-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-[20px] font-semibold text-[var(--text-dark)]">Папки с резюме</h2>
        <button
          type="button"
          onClick={toggleAtsSidebarExpanded}
          aria-label={isAtsSidebarExpanded ? 'Сузить панель папок' : 'Расширить панель папок'}
          title={isAtsSidebarExpanded ? 'Сузить панель папок' : 'Расширить панель папок'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--text-tertiary)] hover:bg-white hover:text-[var(--text-dark)]"
        >
          {isAtsSidebarExpanded ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
        </button>
      </div>

      <div className="mb-3 flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-sidebar-border bg-white px-3 focus-within:border-[#D65F49]">
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
        <input
          type="search"
          aria-label="Поиск по папкам"
          placeholder="Найти папку"
          value={folderQuery}
          onChange={(event) => changeFolderQuery(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Escape') changeFolderQuery(''); }}
          className="min-w-0 flex-1 bg-transparent py-3 text-[14px] outline-none [&::-webkit-search-cancel-button]:appearance-none"
        />
        {folderQuery && <button type="button" aria-label="Очистить поиск папок" onClick={() => changeFolderQuery('')} className="shrink-0 rounded p-1 hover:bg-[#F5F4EF]"><XMarkIcon className="h-4 w-4" /></button>}
      </div>

      <div className="relative mb-5 shrink-0">
        <button
          type="button"
          onClick={() => setSortOpen((current) => !current)}
          className="flex h-11 w-full items-center justify-between rounded-xl border border-sidebar-border bg-white px-4 text-[14px] font-medium text-[var(--text-dark)] shadow-sm"
        >
          <span className="flex min-w-0 items-center gap-2">
            <FunnelIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
            <span className="truncate">
              {sortMode === 'updated'
                ? 'по дате обновления'
                : sortMode === 'nameDesc'
                  ? 'Вакансии по алфавиту (Я→А)'
                  : 'Вакансии по алфавиту'}
            </span>
          </span>
          <span className="text-[var(--text-tertiary)]">⌄</span>
        </button>
        {sortOpen && (
          <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 overflow-hidden rounded-xl border border-sidebar-border bg-white p-1 shadow-[0_12px_32px_rgba(21,22,24,0.14)]">
            {[
              { id: 'updated' as const, label: 'по дате обновления' },
              { id: 'nameAsc' as const, label: 'Вакансии по алфавиту' },
              { id: 'nameDesc' as const, label: 'Вакансии по алфавиту (Я→А)' },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  setSortMode(option.id);
                  setSortOpen(false);
                }}
                className={`flex h-9 w-full items-center rounded-lg px-3 text-left text-[14px] ${
                  sortMode === option.id
                    ? 'bg-[#F5F4EF] font-semibold text-[var(--text-dark)]'
                    : 'text-[var(--text-medium)] hover:bg-[#F8F7F3]'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && sortedFolders.length === 0 && (
        <div className="py-4 text-center text-[14px] text-[var(--text-tertiary)]">
          Загрузка...
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-[#FFF2F0] p-3 text-[13px] text-[#D92D20]">
          {error}
        </div>
      )}

      {!loading && !error && sortedFolders.length === 0 && (
        <div className="py-4 text-center text-[14px] text-[var(--text-tertiary)]">
          Нет папок
        </div>
      )}

      {!loading && !error && searching && visibleFolders.length === 0 && (
        <div role="status" className="py-4 text-center text-[14px] text-[var(--text-tertiary)]">Папки не найдены</div>
      )}

      {visibleFolders.length > 0 && (
        <div className="custom-scrollbar -mx-2 min-h-0 flex-1 overflow-y-auto pb-5">
          <div className="space-y-1 px-2">
            {visibleFolders.map((folder) => (
              <FolderTreeItem
                key={folder.path}
                node={folder}
                level={0}
                activeFolder={activeFolder}
                expandedPaths={visibleExpandedPaths}
                onSelect={selectFolder}
                onToggle={toggleFolder}
                onRename={renameFolder}
                onDelete={openDeleteFolder}
                onCreateChild={(folder) => openCreateFolder(folder.path === ALL_COUNTRIES_FOLDER ? null : folder)}
                onMove={openMoveFolder}
                onImport={importIntoFolder}
                openMenuPath={openMenuPath}
                onToggleMenu={(folderPath) => setOpenMenuPath((current) => current === folderPath ? null : folderPath)}
                searching={searching}
              />
            ))}
          </div>
        </div>
      )}

      {folderAction && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/35 p-4">
          <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_24px_64px_rgba(21,22,24,0.25)]">
            <h3 className="text-lg font-bold text-[#23252D]">
              Новая папка
            </h3>
            <p className="mt-2 text-sm text-[#6B7280]">Родительская папка: {folderAction.folder?.name || 'корень'}</p>
            <input autoFocus value={folderName} onChange={(event) => setFolderName(event.target.value)} placeholder="Название папки" className="mt-4 h-11 w-full rounded-lg border border-[#DDDCD6] px-3 text-sm outline-none focus:border-[#D65F49]" />
            <div className="mt-6 flex justify-end gap-2"><button type="button" onClick={() => setFolderAction(null)} className="h-10 rounded-lg border border-[#DDDCD6] px-4 text-sm">Отмена</button><button type="button" disabled={loading || !folderName.trim()} onClick={() => void submitFolderCreate()} className="h-10 rounded-lg bg-[#D65F49] px-4 text-sm font-semibold text-white disabled:opacity-50">Создать</button></div>
          </div>
        </div>
      )}
      {folderToDelete && (
        <FolderDeleteModal
          folderName={folderToDelete.name}
          folderPath={folderToDelete.path}
          candidates={deleteModalCandidates}
          folders={folders}
          disabled={loading}
          onClose={() => setFolderToDelete(null)}
          onMoveSelected={(candidateIds, targetFolderPath) => void moveSelectedAndDeleteFolder(candidateIds, targetFolderPath)}
          onDeleteWithResumes={() => void deleteFolderWithResumes()}
        />
      )}
      {folderToMove && (
        <FolderMoveModal
          folders={sortedFolders}
          currentFolder={folderToMove.path}
          disabled={loading}
          title={`Перенести «${folderToMove.name}»`}
          description={folderToMove.path.split('/').length === 3
            ? 'Выберите проект, куда перенести вакансию.'
            : 'Выберите страну, куда перенести папку.'}
          selectionLabel={folderToMove.path.split('/').length === 3 ? 'Новый проект' : 'Новая страна'}
          onClose={() => setFolderToMove(null)}
          onSubmit={(targetParentPath) => void submitFolderMove(targetParentPath)}
          isSelectable={(node) => (
            node.path.split('/').length === folderToMove.path.split('/').length - 1
            && node.path !== folderToMove.path.split('/').slice(0, -1).join('/')
          )}
        />
      )}
    </div>
  );
}
